import { useEffect, useRef, useState } from 'react';
import StatusWord, { Dot } from '../../components/StatusWord.jsx';

/**
 * The people picker of Delegate and "Who could answer this?" (spec 2026-10-29 section 2 item 6). Typing a name or an
 * email address suggests people of the customer's own directory (the server searches; this never decides who may
 * review). Picking a suggestion, or pressing Enter on an email address that belongs to a directory person, makes a
 * chip. A person without an onGuard account carries "account will be created". An address that is not in the directory,
 * or a person who cannot review, becomes an error chip with the reason, and the form refuses to send it.
 *
 * A standard combobox: arrows move through the suggestions, Enter picks, Escape closes the list, Backspace in an empty
 * field removes the last chip. `multiple` lets the field hold several chips (an item has one reviewer, so Delegate and
 * "Who could answer this?" use one). The value sent to the API is the directory id only (pickedId), never typed text.
 *
 * `search(q)` resolves { status, body: { people, directoryUnavailable } } like the review adapters. `labelHidden` keeps the
 * label for screen readers only (a table row already names the field); `inputRef` receives the text field (initial focus).
 */

export const NO_ACCOUNT_LINE = 'account will be created';
export const NOT_IN_DIRECTORY = 'Not a member of your directory.';
export const REVIEWED_BEFORE = 'Reviewed this before';
export const MIN_QUERY = 2;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Why a person cannot review, in words (the API's reason codes: reviewerRefusal, 'you' and 'already-reviewer'). */
export const REASON_WORDS = Object.freeze({
  'not-found': NOT_IN_DIRECTORY,
  disabled: 'This account is disabled.',
  guest: 'A guest account cannot review.',
  'no-address': 'This account has no email address, so onGuard cannot send it a review.',
  'no-verified-domains': 'onGuard could not read your verified domains from Microsoft yet. Try again in a few minutes.',
  'address-outside-verified-domains': 'Their email address is not in one of your verified domains.',
  self: 'They hold this access, and nobody reviews their own access.',
  'member-of-group': 'They are in the group that holds this access, and nobody reviews their own access.',
  owner: 'They own the app or group that holds this access.',
  you: 'That is you. Choose someone else.',
  'already-reviewer': 'They already review this assignment.',
});
export const reasonWords = (reason) => REASON_WORDS[reason] ?? 'This person cannot review.';

/** A chip from a suggestion. */
export const pillOf = (person) => ({
  key: `id:${person.directoryUserId}`,
  directoryUserId: person.directoryUserId,
  displayName: person.displayName,
  mail: person.mail ?? null,
  hasAccount: person.hasAccount ?? null,
  error: person.canReview ? null : reasonWords(person.reason),
});
const typedPill = (text) => ({ key: `typed:${text}`, directoryUserId: null, displayName: text, mail: null, hasAccount: null, error: NOT_IN_DIRECTORY });

/** The directory id of the one chip that can be sent, or null (no chip, or an error chip). */
export const pickedId = (pills) => (Array.isArray(pills) && pills.length === 1 && !pills[0].error ? pills[0].directoryUserId : null);
/** The first error chip's words, or null. */
export const pillError = (pills) => (Array.isArray(pills) ? pills.find((p) => p.error)?.error ?? null : null);

export default function PeoplePicker({ id, label, labelHidden = false, hint = null, search, value = [], onChange, multiple = false, disabled = false, delay = 250, inputRef: outerInputRef = null }) {
  const [text, setText] = useState('');
  const [results, setResults] = useState({ q: '', people: [], directoryUnavailable: false, failed: null });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const latest = useRef('');
  const timer = useRef(null);
  const listId = `${id}-list`;
  const hintId = hint ? `${id}-hint` : undefined;
  const optionId = (i) => `${id}-option-${i}`;
  const full = !multiple && value.length > 0;
  const query = text.trim();

  useEffect(() => () => clearTimeout(timer.current), []);

  const run = async (q) => {
    latest.current = q;
    setBusy(true);
    let answer;
    try { answer = await search(q); } catch { answer = null; }
    if (latest.current !== q) return null;
    setBusy(false);
    const ok = answer?.status === 200 && Array.isArray(answer.body?.people);
    const next = ok
      ? { q, people: answer.body.people, directoryUnavailable: answer.body.directoryUnavailable === true, failed: null }
      : { q, people: [], directoryUnavailable: false, failed: answer?.status === 429 ? 'Too many searches. Wait a moment and type again.' : 'The directory search did not answer. Try again in a moment.' };
    setResults(next);
    setActive(next.people.length > 0 ? 0 : -1);
    setOpen(next.people.length > 0);
    return next;
  };

  const onText = (v) => {
    setText(v);
    clearTimeout(timer.current);
    const q = v.trim();
    if (q.length < MIN_QUERY) { latest.current = ''; setBusy(false); setOpen(false); setActive(-1); setResults({ q: '', people: [], directoryUnavailable: false, failed: null }); return; }
    timer.current = setTimeout(() => { run(q); }, delay);
  };

  const add = (pill) => {
    const rest = multiple ? value.filter((p) => p.key !== pill.key) : [];
    onChange([...rest, pill]);
    setText('');
    setOpen(false);
    setActive(-1);
    setResults({ q: '', people: [], directoryUnavailable: false, failed: null });
    latest.current = '';
    clearTimeout(timer.current);
  };
  const remove = (key) => {
    onChange(value.filter((p) => p.key !== key));
    setTimeout(() => inputRef.current?.focus?.(), 0);
  };

  // Enter on typed text: an email address that matches a directory person becomes their chip; one that does not becomes
  // an error chip. Anything else waits for a suggestion to be picked.
  const commitTyped = async () => {
    const typed = query.toLowerCase();
    if (!EMAIL_RE.test(typed)) return;
    clearTimeout(timer.current);
    const known = results.q === query ? results : await run(query);
    if (!known) return;
    if (known.failed) return;
    // Only the server's `exact` flag names the person a typed address belongs to (a person who cannot review comes back
    // without an address); anything else is "not in your directory".
    const hit = known.people.find((p) => p.exact === true);
    add(hit ? pillOf(hit) : typedPill(typed));
  };

  const onKeyDown = (e) => {
    const n = results.people.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open && n > 0) { setOpen(true); setActive(0); return; }
      if (n > 0) setActive((a) => (a + 1) % n);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (n > 0) setActive((a) => (a <= 0 ? n - 1 : a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && active >= 0 && active < n) add(pillOf(results.people[active]));
      else commitTyped();
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); e.stopPropagation(); setOpen(false); setActive(-1); }
    } else if (e.key === 'Backspace' && text === '' && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  const status = busy ? 'Searching your directory.'
    : results.failed ? results.failed
      : results.q && results.q === query ? (results.people.length === 0 ? `No one in your directory matches ${results.q}.` : `${results.people.length} ${results.people.length === 1 ? 'person' : 'people'} found. Use the arrow keys to choose.`)
        : '';

  return (
    <div className="space-y-1.5" data-testid={`${id}-picker`}>
      <label className={labelHidden ? 'sr-only' : 'label'} htmlFor={`${id}-input`}>{label}</label>
      <div className={`flex flex-wrap items-center gap-1.5 rounded-sm border border-ipc-field bg-white px-2 py-1.5 ${disabled ? 'opacity-60' : ''}`}>
        {value.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5" aria-label={`${label}: chosen`}>
            {value.map((p) => (
              <li
                key={p.key} data-testid={`${id}-pill`} data-error={p.error ? 'true' : 'false'}
                className={`inline-flex max-w-full items-start gap-2 rounded-sm border px-2.5 py-1 text-sm text-ipc-ink ${p.error ? 'border-ipc-bad bg-white' : 'border-ipc-border bg-ipc-surface'}`}
              >
                <span className="min-w-0">
                  <span className="block font-semibold">{p.displayName}</span>
                  {p.mail && p.mail !== p.displayName ? <span className="block break-all text-[12.5px] text-ipc-muted">{p.mail}</span> : null}
                  {p.error ? <span className="block text-[12.5px]"><StatusWord tone="bad" strong={false}>{p.error}</StatusWord></span> : null}
                  {!p.error && p.hasAccount === false ? <span className="block text-[12.5px] text-ipc-muted" data-testid={`${id}-no-account`}>{NO_ACCOUNT_LINE}</span> : null}
                </span>
                <button
                  type="button" className="tap shrink-0 text-sm font-semibold text-ipc-light hover:underline" disabled={disabled}
                  aria-label={`Remove ${p.displayName}`} onClick={() => remove(p.key)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {full ? null : (
          <input
            ref={(el) => { inputRef.current = el; if (outerInputRef) outerInputRef.current = el; }} id={`${id}-input`} type="text" className="min-h-[32px] min-w-[12rem] flex-1 border-0 bg-transparent px-1 text-sm text-ipc-ink focus:outline-none"
            role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={listId}
            aria-activedescendant={open && active >= 0 ? optionId(active) : undefined} aria-describedby={hintId}
            autoComplete="off" spellCheck={false} disabled={disabled} value={text}
            onChange={(e) => onText(e.target.value)} onKeyDown={onKeyDown} onBlur={() => setOpen(false)}
            onFocus={() => { if (results.people.length > 0 && results.q === query) setOpen(true); }}
            data-testid={`${id}-input`}
          />
        )}
      </div>
      {open ? (
        <ul id={listId} role="listbox" aria-label={`${label}: suggestions`} className="max-h-72 overflow-y-auto rounded-sm border border-ipc-border bg-white" data-testid={`${id}-list`}>
          {results.people.map((p, i) => (
            <li
              key={p.directoryUserId} id={optionId(i)} role="option" aria-selected={i === active}
              className={`cursor-pointer px-3 py-2 text-sm text-ipc-ink ${i === active ? 'bg-ipc-surface' : ''}`}
              onMouseDown={(e) => e.preventDefault()} onClick={() => add(pillOf(p))} onMouseEnter={() => setActive(i)}
            >
              <span className="flex flex-wrap items-center gap-x-2">
                <span className="font-semibold">{p.displayName}</span>
                {p.reviewedBefore ? <span className="inline-flex items-center gap-1.5 text-[12.5px]"><Dot tone="info" />{REVIEWED_BEFORE}</span> : null}
              </span>
              <span className="block break-all text-[12.5px] text-ipc-muted">{[p.mail, p.department].filter(Boolean).join(', ')}</span>
              {p.canReview ? null : <span className="block text-[12.5px]"><StatusWord tone="bad" strong={false}>{reasonWords(p.reason)}</StatusWord></span>}
            </li>
          ))}
        </ul>
      ) : null}
      {results.directoryUnavailable && results.q === query ? (
        <p className="text-[12.5px] text-ipc-muted" data-testid={`${id}-partial`}>Only people onGuard already knows are listed: your directory could not be searched just now.</p>
      ) : null}
      {hint ? <p id={hintId} className="text-[12.5px] text-ipc-muted">{hint}</p> : null}
      <p className="sr-only" role="status" aria-live="polite">{status}</p>
    </div>
  );
}
